'use client';
import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import DailyBehaviorForm from './components/DailyBehaviorForm';
import CorrelationCard from './components/CorrelationCard';
import AfterHoursAnalysisCard from './components/AfterHoursAnalysisCard';
import SleepTrainingCorrelation from './components/SleepTrainingCorrelation';
import WeeklyInsightReport from './components/WeeklyInsightReport';
import BehaviorDashboard from './components/BehaviorDashboard';
import {
  forgeBehaviorService,
  BehaviorLog,
  BehaviorCorrelation,
  AfterHoursAnalysis,
  WeeklyInsight,
} from '@/lib/services/forgeBehaviorService';

type Tab = 'log' | 'correlations' | 'dashboard' | 'insights';

const TABS: { id: Tab; label: string }[] = [
  { id: 'log', label: 'Daily Log' },
  { id: 'correlations', label: 'Correlations' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'insights', label: 'Insights' },
];

const BEHAVIOR_KEYS: { key: keyof BehaviorLog; label: string }[] = [
  { key: 'cannabisAmount', label: 'Cannabis' },
  { key: 'alcoholLevel', label: 'Alcohol' },
  { key: 'hadLateNightEating', label: 'Late Night Eating' },
  { key: 'hadTakeaway', label: 'Takeaway' },
  { key: 'wasHighStress', label: 'High Stress' },
  { key: 'hadSocialEvent', label: 'Social Event' },
  { key: 'hadEnergyDrink', label: 'Energy Drink' },
  { key: 'hadPoorSleepPrevious', label: 'Poor Sleep (Prev)' },
];

export default function BehaviorIntelligencePage() {
  const [activeTab, setActiveTab] = useState<Tab>('log');
  const [logs, setLogs] = useState<BehaviorLog[]>([]);
  const [correlations, setCorrelations] = useState<BehaviorCorrelation[]>([]);
  const [afterHours, setAfterHours] = useState<AfterHoursAnalysis | null>(null);
  const [weeklyInsight, setWeeklyInsight] = useState<WeeklyInsight | null>(null);
  const [frequency, setFrequency] = useState<ReturnType<typeof forgeBehaviorService.getBehaviorFrequency> | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    const allLogs = await forgeBehaviorService.getAllLogs();
    setLogs(allLogs);

    const corrs: BehaviorCorrelation[] = [];
    for (const { key, label } of BEHAVIOR_KEYS) {
      const c = forgeBehaviorService.computeCorrelation(allLogs, key, label);
      if (c) corrs.push(c);
    }
    setCorrelations(corrs);
    setAfterHours(forgeBehaviorService.computeAfterHoursAnalysis(allLogs));
    setWeeklyInsight(forgeBehaviorService.computeWeeklyInsights(allLogs));
    setFrequency(forgeBehaviorService.getBehaviorFrequency(allLogs));
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <AppLayout activeRoute="/behavior-intelligence">
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-primary rounded-full" />
            <div>
              <h1 className="text-2xl font-black tracking-widest text-foreground uppercase">
                Behavior Intelligence
              </h1>
              <p className="text-xs text-muted-foreground tracking-wider">
                Insights → Behavior Intelligence
              </p>
            </div>
          </div>
          <div className="ml-4 pl-3 border-l border-border">
            <p className="text-xs text-muted-foreground/70 italic">
              The forge reveals the truth. Measure first. Decide second.
            </p>
          </div>
        </div>

        {/* Philosophy banner */}
        <div className="bg-card-elevated border border-border rounded-lg px-5 py-3 flex items-center gap-4">
          <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-green shrink-0" />
          <p className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">FORGE does not judge behavior.</span>{' '}
            FORGE reveals patterns. The user decides what to do with the truth.
            All insights are observations only — correlation does not equal causation.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-card-elevated rounded-lg p-1 w-full sm:w-fit overflow-x-auto scrollbar-forge">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-md text-xs font-bold tracking-wider transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary/15 text-primary border border-primary/20' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'log' && (
          <DailyBehaviorForm onSaved={loadData} />
        )}

        {activeTab === 'correlations' && (
          <div className="space-y-4 sm:space-y-6">
            {loading ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="card-forge p-5 animate-pulse space-y-3">
                    <div className="h-5 bg-card-elevated rounded w-40" />
                    <div className="space-y-2">
                      {[1, 2, 3, 4, 5].map(j => <div key={j} className="h-4 bg-card-elevated rounded" />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : correlations.length === 0 ? (
              <div className="card-forge p-8 text-center space-y-3">
                <div className="text-4xl">◆</div>
                <h3 className="text-forge-heading font-bold text-lg">Not Enough Data Yet</h3>
                <p className="text-muted-foreground text-sm max-w-md mx-auto">
                  Log at least 2 days with a behavior and 2 days without to see correlation comparisons.
                  Keep logging daily to build your intelligence report.
                </p>
                <p className="text-xs text-muted-foreground/60 italic">
                  Weight is feedback. Compliance is the scoreboard.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5">
                  {correlations.map(c => (
                    <CorrelationCard key={c.behavior} correlation={c} />
                  ))}
                </div>
                <AfterHoursAnalysisCard analysis={afterHours!} />
                <SleepTrainingCorrelation logs={logs} />
              </>
            )}
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => <div key={i} className="card-forge p-5 animate-pulse h-32" />)}
              </div>
            ) : frequency ? (
              <BehaviorDashboard logs={logs} frequency={frequency} />
            ) : (
              <div className="card-forge p-8 text-center">
                <p className="text-muted-foreground">Start logging behaviors to see your dashboard.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="space-y-5">
            {loading ? (
              <div className="card-forge p-5 animate-pulse h-48" />
            ) : weeklyInsight ? (
              <>
                <WeeklyInsightReport insight={weeklyInsight} />

                {/* Personalized recommendations */}
                {weeklyInsight.hasEnoughData && (
                  <div className="card-forge p-5 space-y-4">
                    <h3 className="text-forge-heading font-bold tracking-wider text-sm uppercase">Personalized Observations</h3>
                    <div className="space-y-2">
                      {weeklyInsight.cannabisNeutralToWeightLoss && (
                        <div className="flex gap-3 items-start p-3 bg-card-elevated rounded-lg">
                          <span className="text-muted-foreground mt-0.5 shrink-0">→</span>
                          <p className="text-sm text-foreground">Cannabis appears neutral to your weight-loss progress.</p>
                        </div>
                      )}
                      {weeklyInsight.cannabisAssociatedWithExtraCalories && (
                        <div className="flex gap-3 items-start p-3 bg-card-elevated rounded-lg">
                          <span className="text-amber-400 mt-0.5 shrink-0">→</span>
                          <p className="text-sm text-foreground">Cannabis appears associated with increased unplanned calories.</p>
                        </div>
                      )}
                      {weeklyInsight.cannabisAssociatedWithBetterSleep && (
                        <div className="flex gap-3 items-start p-3 bg-card-elevated rounded-lg">
                          <span className="text-blue-400 mt-0.5 shrink-0">→</span>
                          <p className="text-sm text-foreground">Cannabis appears associated with improved sleep consistency.</p>
                        </div>
                      )}
                      {weeklyInsight.cannabisAssociatedWithLowerProtein && (
                        <div className="flex gap-3 items-start p-3 bg-card-elevated rounded-lg">
                          <span className="text-red-400 mt-0.5 shrink-0">→</span>
                          <p className="text-sm text-foreground">Cannabis appears associated with lower daily protein intake.</p>
                        </div>
                      )}
                      {!weeklyInsight.cannabisNeutralToWeightLoss && !weeklyInsight.cannabisAssociatedWithExtraCalories && !weeklyInsight.cannabisAssociatedWithBetterSleep && !weeklyInsight.cannabisAssociatedWithLowerProtein && (
                        <p className="text-sm text-muted-foreground py-2">No strong patterns detected yet. Continue logging for more precise observations.</p>
                      )}
                    </div>
                    <p className="text-2xs text-muted-foreground/50 italic pt-1 border-t border-border">
                      Recommendations are data-based observations only. FORGE never moralizes, never shames, and never recommends quitting or increasing any behavior.
                      The purpose is awareness. The forge reveals the truth. Measure first. Decide second.
                    </p>
                  </div>
                )}

                {/* Forge philosophy */}
                <div className="card-forge p-5 border border-primary/10 space-y-3">
                  <div className="text-center space-y-2">
                    <p className="text-sm font-bold text-foreground tracking-wider">THE FORGE PHILOSOPHY</p>
                    <p className="text-xs text-muted-foreground italic">
                      "The forge reveals the truth. Measure first. Decide second."
                    </p>
                    <p className="text-xs text-muted-foreground italic">
                      "Weight is feedback. Compliance is the scoreboard."
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border">
                    <div className="text-center">
                      <div className="text-xs font-bold text-primary">MEASURE</div>
                      <div className="text-2xs text-muted-foreground mt-0.5">Track behaviors objectively</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-bold text-primary">REVEAL</div>
                      <div className="text-2xs text-muted-foreground mt-0.5">See patterns in data</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-bold text-primary">DECIDE</div>
                      <div className="text-2xs text-muted-foreground mt-0.5">You choose what to do</div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
